import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/shared/api/client';
import { Alert } from '@/shared/components/Alert';
import { AuthShell } from '@/shared/components/AuthShell';
import { Button } from '@/shared/components/Button';
import { FieldError } from '@/shared/components/Field';
import { resolveErrorMessage } from '@/shared/components/States';
import { useLocale } from '@/shared/i18n/useLocale';

import { isSignInStep, organisationName, type OrganisationSelection } from './organisationChoice';
import { useChooseOrganisation } from './useSession';

/**
 * A choice that can no longer be used: expired, already used, or never issued
 * to this browser — the server answers all of them alike (401
 * `LOGIN_SELECTION_INVALID`), and so does the session ending under it (419).
 */
function isSpent(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 419);
}

/** The chosen organisation refused the sign-in: locked, inactive, archived. */
function isRefused(error: unknown): boolean {
  return error instanceof ApiError && error.isForbidden;
}

/**
 * The buyer's sign-in, step 2: which organisation to open.
 *
 * Shown only when the address and password opened two or more organisations
 * (auth contract §1). Nothing is preselected — opening the wrong organisation
 * by accident is the risk this step exists to prevent — and the first choice
 * has focus, so the arrows move within the list, Tab leaves it and Enter
 * continues. The choice is posted with the step-1 token and nothing else.
 *
 * Its state is this screen's own (`LoginPage`): never the session cache, the
 * URL or storage. A choice that can no longer be used says so and goes back
 * to step 1; a refused organisation goes back to step 1 with the reason; a
 * throttled or failed request keeps the choice on screen to try again.
 */
export function OrganisationChoice({
  email,
  selection,
  onBack,
  onExpired,
  onRefused,
}: {
  /** Whose sign-in this is — the address typed at step 1. */
  email: string;
  selection: OrganisationSelection;
  /** "Use a different account": back to step 1, sending nothing. */
  onBack: () => void;
  onExpired: () => void;
  onRefused: (error: unknown) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const choose = useChooseOrganisation();
  const [chosen, setChosen] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const errorId = useId();

  const missing = touched && chosen === null;

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);

    if (chosen === null || choose.isPending) return;

    choose.mutate(
      { selection_token: selection.token, organisation: chosen },
      {
        // A session is cached by the hook and the gate takes over from here;
        // a further step is nothing this screen can continue.
        onSuccess: (result) => {
          if (isSignInStep(result)) onExpired();
        },
        onError: (error) => {
          if (isSpent(error)) onExpired();
          else if (isRefused(error)) onRefused(error);
        },
      },
    );
  };

  const failure =
    choose.error && !isSpent(choose.error) && !isRefused(choose.error) ? choose.error : null;

  return (
    <AuthShell
      identity="tenant"
      title={t('auth.organisationChoice.title')}
      subtitle={t('auth.organisationChoice.body')}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Whose sign-in continues: nothing is signed in until a choice is made. */}
          <p className="type-meta min-w-0 text-ink-muted">
            {t('auth.organisationChoice.continuingAs')}{' '}
            <bdi dir="ltr" className="break-all">
              {email}
            </bdi>
          </p>
          <Button variant="ghost" size="sm" disabled={choose.isPending} onClick={onBack}>
            {t('auth.organisationChoice.back')}
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {failure ? <Alert tone="danger">{resolveErrorMessage(failure, t)}</Alert> : null}

        <fieldset aria-describedby={missing ? errorId : undefined}>
          <legend className="type-ui mb-2 font-medium text-ink">
            {t('auth.organisationChoice.legend')}
          </legend>
          <div className="flex flex-col gap-2">
            {selection.organisations.map((organisation, index) => {
              const name = organisationName(organisation, locale);

              return (
                <label
                  key={organisation.id}
                  className="flex min-h-12 cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 ring-1 ring-line has-checked:bg-selected has-checked:ring-primary has-focus-visible:outline-2 has-focus-visible:outline-offset-(--focus-ring-offset) has-focus-visible:outline-focus"
                >
                  <input
                    type="radio"
                    name="organisation"
                    value={organisation.id}
                    required
                    checked={chosen === organisation.id}
                    aria-invalid={missing ? true : undefined}
                    autoFocus={index === 0}
                    className="size-5 shrink-0"
                    onChange={() => setChosen(organisation.id)}
                  />
                  <bdi lang={name.lang} className="type-ui min-w-0 font-medium text-ink">
                    {name.text}
                  </bdi>
                </label>
              );
            })}
          </div>
          {missing ? (
            <FieldError id={errorId} className="mt-2">
              {t('auth.organisationChoice.required')}
            </FieldError>
          ) : null}
        </fieldset>

        <Button type="submit" size="lg" className="w-full" loading={choose.isPending}>
          {t('auth.organisationChoice.continue')}
        </Button>
      </form>
    </AuthShell>
  );
}

/**
 * Step 2 can no longer continue — the choice timed out or was already used.
 * One way on: sign in again, with the address kept.
 */
export function OrganisationChoiceExpired({ onRestart }: { onRestart: () => void }) {
  const { t } = useTranslation();

  return (
    <AuthShell identity="tenant" title={t('auth.organisationChoice.expiredTitle')}>
      <div className="space-y-4">
        <Alert tone="warning">{t('auth.organisationChoice.expiredBody')}</Alert>
        <Button size="lg" className="w-full" autoFocus onClick={onRestart}>
          {t('auth.organisationChoice.signInAgain')}
        </Button>
      </div>
    </AuthShell>
  );
}
